import { useRouter } from "next/router";
import { OrderTypesEnum } from "../../utils/enums";
import { useAppSelector } from "./useReduxHooks";

/**
 * Static dine-in menu (prepits/project#4291).
 *
 * A branch with the `dineInMenuView` franchise flag serves a view-only menu to
 * a guest who scanned its dine-in QR code: browse the menu and item details,
 * but nothing to add, no cart, no rewards and no sign-in prompt.
 *
 * The mode is scoped to the DINE_IN order type on purpose - the same restaurant
 * still takes delivery orders, so switching order type from the burger menu has
 * to hand the guest back the full ordering experience.
 */
export const useDineInMenuView = (): boolean => {
  const router = useRouter();

  const dineInMenuView = useAppSelector(
    (state) => state.config?.selectedFranchise?.franchiseConfig?.dineInMenuView
  );
  // Optional all the way down on purpose: this hook is called from the header,
  // the burger menu and the item card - components that render on every page -
  // so a slice that hasn't rehydrated yet must read as "off", never throw.
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams?.orderTypeParam
  );
  const selectedSlug = useAppSelector(
    (state) => state.config?.selectedFranchise?.slug
  );

  /**
   * The URL decides, not the persisted state. Both `config` and `queryParams`
   * are persisted, so on a fresh load they still describe the PREVIOUS visit -
   * and `useFranchiseFiltering` is about to wipe them precisely when the URL
   * carries no `franchise`/`order_type`. Believing them there put a returning
   * guest who hard-loads `/home` into view-only mode for a render, spending the
   * once-a-visit snackbar over what turns out to be the welcome drawer.
   *
   * `router.query` is also `{}` until `isReady`, which is the same trap, so
   * nothing is decided before then. In-app navigation carries both params
   * forward (getPageLink builds from the current query), so requiring them
   * costs the real flow nothing.
   */
  if (!router.isReady) {
    return false;
  }

  const { order_type: urlOrderType, franchise: urlFranchise } = router.query;

  if (urlOrderType !== OrderTypesEnum.DINE_IN) {
    return false;
  }

  // Persisted branch from the last visit, URL pointing at a different one.
  if (
    typeof urlFranchise === "string" &&
    !!selectedSlug &&
    urlFranchise !== selectedSlug
  ) {
    return false;
  }

  return Boolean(dineInMenuView) && orderTypeParam === OrderTypesEnum.DINE_IN;
};
